import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import "../screenstyles/TechnicalJoinRequests.css";
import { FaArrowLeft } from "react-icons/fa";

interface TechnicianDetails {
  id: string;

  // بيانات users
  AccountLevel?: string;
  accountType?: string;
  email?: string;
  fullName?: string;
  loginType?: string;
  phoneNumber?: string;
  province?: string;
  uid?: string;
  points?: number;
  profileImageUrl?: string;
  requestStatus?: string;
  approvedAt?: any;

  // بيانات requestfortechnicians
  fullAddress?: string;
  emailAddress?: string;
  expertise?: string;
  services?: string;
  years?: string;
  regions?: string;
  partnerships?: string;

  // تواريخ
  createdAt?: any;
  userCreatedAt?: any;
  requestCreatedAt?: any;
  updatedAt?: any;
}

const TechniciansDetails: React.FC = () => {
  const [technicians, setTechnicians] = useState<TechnicianDetails[]>([]);
  const [selectedTechnician, setSelectedTechnician] =
    useState<TechnicianDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setLoading(true);

        const usersRef = collection(db, "users");

        // هنا الفرق المهم: نجلب الحسابات التي أصبحت منصب
        const q = query(usersRef, where("accountType", "==", "منصب"));

        const snapshot = await getDocs(q);

        const data: TechnicianDetails[] = await Promise.all(
          snapshot.docs.map(async (userDoc) => {
            const userData = userDoc.data();

            // جلب بيانات طلب الانضمام القديم من requestfortechnicians
            const requestDocRef = doc(
              db,
              "requestfortechnicians",
              userDoc.id
            );

            const requestDocSnap = await getDoc(requestDocRef);

            const requestData = requestDocSnap.exists()
              ? requestDocSnap.data()
              : {};

            return {
              id: userDoc.id,

              // نخزن التواريخ بأسماء واضحة
              userCreatedAt: userData.createdAt,
              requestCreatedAt: requestData.createdAt,

              // بيانات users
              ...userData,

              // بيانات requestfortechnicians
              // إذا يوجد حقل متشابه، بيانات الطلب تغطي عليه لأنها بيانات الفورم
              ...requestData,

              // توحيد البريد حتى لا يظهر مرتين
              email: requestData.emailAddress || userData.email || "غير متوفر",

              // توحيد التاريخ الرئيسي
              createdAt: requestData.createdAt || userData.createdAt,
            };
          })
        );

        setTechnicians(data);

        if (data.length > 0) {
          setSelectedTechnician(data[0]);
        }
      } catch (error) {
        console.error("Error fetching technicians:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicians();
  }, []);

  const formatDate = (value: any) => {
    if (!value) return "غير متوفر";

    try {
      if (value?.toDate) {
        return value.toDate().toLocaleString("ar-IQ");
      }

      return new Date(value).toLocaleString("ar-IQ");
    } catch {
      return "غير متوفر";
    }
  };

  if (loading) {
    return <div className="technical-page">جاري تحميل بيانات التقنيين...</div>;
  }

  return (
    <div className="technical-page" dir="rtl">
      <div className="technical-header">
        <div className="technical-header-content">
          <h2>تفاصيل التقنيين</h2>
          <p>عرض بيانات التقنيين المقبولين وبيانات طلب الانضمام الخاصة بهم</p>
        </div>

        <button
          className="back-icon-btn"
          onClick={() => navigate("/")}
          title="رجوع"
        >
          <FaArrowLeft />
        </button>
      </div>

      {technicians.length === 0 ? (
        <div className="empty-box">لا توجد حسابات تقنيين حالياً</div>
      ) : (
        <div className="requests-layout">
          <div className="requests-list">
            {technicians.map((technician) => (
              <div
                key={technician.id}
                className={`request-card ${
                  selectedTechnician?.id === technician.id ? "active" : ""
                }`}
                onClick={() => setSelectedTechnician(technician)}
              >
                <img
                  src={technician.profileImageUrl || "/default-user.png"}
                  alt={technician.fullName || "User"}
                  className="request-image"
                />

                <div className="request-info">
                  <h3>{technician.fullName || "بدون اسم"}</h3>
                  <p>{technician.phoneNumber || "لا يوجد رقم هاتف"}</p>
                  <span>{technician.province || "بدون محافظة"}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="request-details">
            {!selectedTechnician ? (
              <div className="select-message">
                اختر تقني من القائمة لعرض التفاصيل بالكامل
              </div>
            ) : (
              <>
                <div className="details-top">
                  <img
                    src={
                      selectedTechnician.profileImageUrl || "/default-user.png"
                    }
                    alt={selectedTechnician.fullName || "User"}
                    className="details-image"
                  />

                  <div>
                    <h2>{selectedTechnician.fullName || "بدون اسم"}</h2>
                    <p>{selectedTechnician.accountType || "غير متوفر"}</p>
                  </div>
                </div>

                <div className="details-grid">
                  <div>
                    <label>الاسم الكامل</label>
                    <p>{selectedTechnician.fullName || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>رقم الهاتف</label>
                    <p>{selectedTechnician.phoneNumber || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>البريد الإلكتروني</label>
                    <p>{selectedTechnician.email || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>المحافظة</label>
                    <p>{selectedTechnician.province || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>العنوان الكامل</label>
                    <p>{selectedTechnician.fullAddress || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>نوع الحساب</label>
                    <p>{selectedTechnician.accountType || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>مستوى الحساب</label>
                    <p>{selectedTechnician.AccountLevel || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>نوع تسجيل الدخول</label>
                    <p>{selectedTechnician.loginType || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>النقاط</label>
                    <p>{selectedTechnician.points ?? 0}</p>
                  </div>

                  <div>
                    <label>حالة الطلب</label>
                    <p>{selectedTechnician.requestStatus || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>الاختصاص الأساسي</label>
                    <p>{selectedTechnician.expertise || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>سنوات الخبرة</label>
                    <p>{selectedTechnician.services || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>الشهادات التقنية</label>
                    <p>{selectedTechnician.years || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>خبرة العمل الميداني</label>
                    <p>{selectedTechnician.regions || "غير متوفر"}</p>
                  </div>

                  <div className="full-width">
                    <label>المناطق التي يغطيها</label>
                    <p>{selectedTechnician.partnerships || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>تاريخ تقديم الطلب</label>
                    <p>{formatDate(selectedTechnician.requestCreatedAt)}</p>
                  </div>

                  <div>
                    <label>تاريخ إنشاء الحساب</label>
                    <p>{formatDate(selectedTechnician.userCreatedAt)}</p>
                  </div>

                  <div>
                    <label>تاريخ الموافقة</label>
                    <p>{formatDate(selectedTechnician.approvedAt)}</p>
                  </div>

                  <div className="full-width">
                    <label>UID</label>
                    <p>{selectedTechnician.uid || selectedTechnician.id}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechniciansDetails;